import { useEffect, useRef, useState } from "react";

// Share of the not-yet-shown text revealed per frame: big chunks catch up fast, small ones type out
const REVEAL_RATE_PER_FRAME = 0.12;
const MIN_CHARS_PER_FRAME = 1;

/**
 * Reveals streamed text smoothly, ChatGPT style, instead of in the bursts the network delivers.
 * Text that was already complete when the component mounted is shown at once.
 */
export function useSmoothStreamText(text: string, isStreaming: boolean): string {
    const [shownLength, setShownLength] = useState(() => (isStreaming ? 0 : text.length));
    const targetLengthRef = useRef(text.length);
    targetLengthRef.current = text.length;

    const isRevealing = shownLength < text.length;

    useEffect(() => {
        if (!isRevealing) return;

        let frame = requestAnimationFrame(function reveal() {
            setShownLength((shown) => {
                const remaining = targetLengthRef.current - shown;
                if (remaining <= 0) return shown;
                return shown + Math.max(MIN_CHARS_PER_FRAME, Math.ceil(remaining * REVEAL_RATE_PER_FRAME));
            });
            frame = requestAnimationFrame(reveal);
        });

        return () => cancelAnimationFrame(frame);
    }, [isRevealing]);

    return text.slice(0, Math.min(shownLength, text.length));
}
